import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Check, Sparkles, Phone, Mail, Loader2, X } from "lucide-react";
import { PLANS_DATA, PLANS_ORDER, getPrice, getPriceLabel } from "../data/plans";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ════════════════════════════════════════════════════════
   Billing Period Toggle
   ════════════════════════════════════════════════════════ */
function BillingToggle({ billingPeriod, onChange }) {
  return (
    <div className="flex justify-center mb-6">
      <div className="inline-flex bg-slate-800 rounded-lg p-1">
        <button
          onClick={() => onChange("monthly")}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            billingPeriod === "monthly"
              ? "bg-teal-600 text-white"
              : "text-slate-400 hover:text-white"
          }`}
        >
          Mensual
        </button>
        <button
          onClick={() => onChange("annual")}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
            billingPeriod === "annual"
              ? "bg-teal-600 text-white"
              : "text-slate-400 hover:text-white"
          }`}
        >
          Anual
          <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded-full font-semibold">
            -18%
          </span>
        </button>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   Plan Card (Compact for Modal)
   ════════════════════════════════════════════════════════ */
function PlanCard({ plan, billingPeriod, onSelect, processing, isCurrentPlan }) {
  const price = getPrice(plan.id, billingPeriod);
  const priceLabel = getPriceLabel(billingPeriod);
  const isEnterprise = plan.isEnterprise;
  const isFree = plan.id === "free";

  return (
    <div
      className={`relative rounded-xl p-5 flex flex-col transition-all duration-200 ${
        plan.highlight
          ? "bg-gradient-to-b from-teal-500/10 to-teal-500/5 border-2 border-teal-500/50"
          : "bg-slate-900/50 border border-slate-700 hover:border-slate-600"
      } ${isCurrentPlan ? "opacity-60" : ""}`}
    >
      {plan.highlight && (
        <div className="absolute -top-2.5 left-1/2 -translate-x-1/2">
          <span className="px-2.5 py-0.5 bg-teal-500 text-white text-xs font-semibold rounded-full flex items-center gap-1">
            <Sparkles size={10} />
            Popular
          </span>
        </div>
      )}

      <div className="mb-3">
        <h3 className="text-lg font-bold text-white flex items-center gap-2">
          {plan.name}
          {isCurrentPlan && <span className="text-xs text-teal-400 font-normal">(Actual)</span>}
        </h3>
        <p className="text-slate-400 text-xs">{plan.description}</p>
      </div>

      <div className="mb-4">
        {isEnterprise ? (
          <span className="text-2xl font-bold text-white">Custom</span>
        ) : isFree ? (
          <>
            <span className="text-3xl font-bold text-white">$0</span>
            <span className="text-slate-400 text-xs ml-1">gratis</span>
          </>
        ) : (
          <>
            <span className="text-3xl font-bold text-white">${price}</span>
            <span className="text-slate-400 text-xs ml-1">{priceLabel}</span>
            {plan.minSeats > 1 && (
              <p className="text-xs text-slate-500 mt-1">Mín. {plan.minSeats} usuarios</p>
            )}
          </>
        )}
      </div>

      {/* Key features (show first 4) */}
      <ul className="space-y-2 mb-4 flex-1">
        {plan.features.slice(0, 4).map((feature, idx) => (
          <li key={idx} className="flex items-start gap-2 text-xs text-slate-300">
            <Check size={14} className="text-teal-400 flex-shrink-0 mt-0.5" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      <Button
        onClick={() => onSelect(plan.id, isEnterprise)}
        disabled={processing || isCurrentPlan}
        className={`w-full h-9 text-sm ${
          plan.highlight
            ? "bg-teal-600 hover:bg-teal-500 text-white"
            : "bg-slate-800 hover:bg-slate-700 text-white"
        }`}
      >
        {isCurrentPlan ? "Plan actual" : isEnterprise ? "Contactar ventas" : "Seleccionar"}
      </Button>
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   Contact Sales Modal
   ════════════════════════════════════════════════════════ */
function ContactSalesModal({ onClose }) {
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-md w-full p-8 text-center">
        <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-6">
          <Phone className="w-8 h-8 text-emerald-400" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-3">Contacta a nuestro equipo</h2>
        <p className="text-slate-400 mb-6">
          Nuestro equipo de ventas creará una solución personalizada para tu organización.
        </p>
        <div className="bg-slate-800/50 rounded-xl p-4 mb-6 space-y-3">
          <a href="mailto:ventas@notalia.mx" className="flex items-center justify-center gap-2 text-teal-400 hover:text-teal-300">
            <Mail size={18} /> ventas@notalia.mx
          </a>
          <a href="tel:+525512345678" className="flex items-center justify-center gap-2 text-teal-400 hover:text-teal-300">
            <Phone size={18} /> +52 55 1234 5678
          </a>
        </div>
        <Button onClick={onClose} className="w-full bg-teal-600 hover:bg-teal-500 text-white h-11">
          Entendido
        </Button>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   Main Upgrade Modal
   ════════════════════════════════════════════════════════ */
export default function UpgradeModal({ isOpen, onClose, currentPlan = "free" }) {
  const [billingPeriod, setBillingPeriod] = useState("monthly");
  const [loading, setLoading] = useState(false);
  const [showContactSales, setShowContactSales] = useState(false);
  const [error, setError] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [showCoupon, setShowCoupon] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setError("");
      setCouponCode("");
      setShowCoupon(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSelectPlan = async (planId, isEnterprise) => {
    if (isEnterprise) {
      setShowContactSales(true);
      return;
    }

    if (planId === currentPlan) {
      return; // Already on this plan
    }

    setLoading(true);
    setError("");

    try {
      const plan = PLANS_DATA[planId];

      // Free plan - direct downgrade
      if (planId === "free") {
        await axios.post(
          `${API}/billing/cancel-subscription`,
          {},
          { withCredentials: true }
        );
        window.location.reload(); // Refresh to update plan
        return;
      }

      // Paid plans - create Stripe checkout
      const payload = {
        plan: planId,
        billing_period: billingPeriod,
        quantity: plan.minSeats || 1,
        coupon_code: couponCode || null,
        origin_url: window.location.origin,
      };

      const response = await axios.post(`${API}/billing/checkout`, payload, {
        withCredentials: true,
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (err) {
      console.error("Checkout error:", err);
      setError(err.response?.data?.detail || "Error al procesar el pago");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
        <div className="bg-slate-950 border border-slate-800 rounded-2xl max-w-6xl w-full my-8 max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-slate-950 border-b border-slate-800 px-6 py-4 flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white">Planes y Precios</h2>
              <p className="text-slate-400 text-sm">Elige el plan perfecto para tu organización</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {error && (
              <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-lg text-sm mb-6">
                {error}
              </div>
            )}

            <BillingToggle billingPeriod={billingPeriod} onChange={setBillingPeriod} />

            {/* Coupon Code */}
            {showCoupon ? (
              <div className="max-w-md mx-auto mb-6">
                <Input
                  placeholder="Código de cupón"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  className="bg-slate-900 border-slate-700 text-white"
                />
              </div>
            ) : (
              <button
                onClick={() => setShowCoupon(true)}
                className="block mx-auto text-sm text-teal-400 hover:text-teal-300 mb-6"
              >
                ¿Tienes un cupón?
              </button>
            )}

            {/* Plans Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {PLANS_ORDER.map((planId) => (
                <PlanCard
                  key={planId}
                  plan={PLANS_DATA[planId]}
                  billingPeriod={billingPeriod}
                  onSelect={handleSelectPlan}
                  processing={loading}
                  isCurrentPlan={planId === currentPlan}
                />
              ))}
            </div>

            {/* FAQ or additional info */}
            <div className="mt-8 text-center text-xs text-slate-500">
              <p>Todos los planes incluyen soporte por email • Cancela cuando quieras</p>
            </div>
          </div>
        </div>
      </div>

      {showContactSales && <ContactSalesModal onClose={() => setShowContactSales(false)} />}
    </>
  );
}
